import { useFlag } from '@calendly/feature-check-react';
import { I18n } from '@calendly/i18n';
import { BareButton } from '@calendly/ui/components/bare-button';
import { DropdownMenuGroup, DropdownMenuGroupTitle, DropdownMenuItem, useDropdownMenu } from '@calendly/ui/components/dropdown-menu';
import { IconApps } from '@calendly/ui/components/glyphs/IconApps';
import { IconBrowser } from '@calendly/ui/components/glyphs/IconBrowser';
import { IconChartBar } from '@calendly/ui/components/glyphs/IconChartBar';
import { IconClick } from '@calendly/ui/components/glyphs/IconClick';
import { IconCode } from '@calendly/ui/components/glyphs/IconCode';
import { IconExternalLink } from '@calendly/ui/components/glyphs/IconExternalLink';
import { IconLock } from '@calendly/ui/components/glyphs/IconLock';
import { IconLogout } from '@calendly/ui/components/glyphs/IconLogout';
import { IconUserSquareRounded } from '@calendly/ui/components/glyphs/IconUserSquareRounded';
import { BodyText, LabelText } from '@calendly/ui/components/typography';
import { UiIcon, type UiIconGlyphComponent } from '@calendly/ui/components/ui-icon';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { colorIconSecondary, colorIconStandard } from '@calendly/ui/theme';
import { useCallback, useMemo, type FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { moveToCheckout } from 'actions/billing/subscription_modal';
import { useHasSeenNotification, useUserSessionLogout } from 'features/admin';
import { DEVELOPER_DOCS_URL, INTEGRATIONS_PATH, ORGANIZATION_REPORTING_PATH, PROFILE_APP_PATH_LEGACY, SUPER_ADMIN_HREF, VISIT_MARKETING_SITE_URL } from 'features/app_shell/main_nav/navigationLinkConstants';
import { useProductRefreshSettingsIa } from 'features/app_shell/product_refresh/useProductRefreshSettingsIa';
import { UI_THEME_FLAG_KEY } from 'features/app_shell/shell_layout/shellConstants';
import { BILLING_ENTRYPOINTS, setAnalyticsLocation } from 'features/billing';
import { selectProfileById, selectProfileIds, useSchedulingFetchProfilesQuery } from 'features/scheduling';
import { confirmSettingsEntryNavigation } from 'features/settings_modal/confirm_settings_entry_navigation';
import { SETTINGS_SECTION } from 'features/settings_modal/constants';
import { useSettingsNavigation } from 'features/settings_modal/use_settings_navigation';
import { TRACKING_EVENT_NAMES, navigationAnalyticsIdForProfileDropdown, trackProfileDropdownNavigation, useTrack } from 'hooks/analytics_events';
import { isAdminOrOwnerSelector } from 'selectors/current_user';
import { ProfileThemeRow } from './ProfileThemeRow';
export const PROFILE_MENU_I18N_KEYS = {
  profile: 'publisher.personal_settings.navigation.profile',
  landingPage: 'publisher.app_shell.profile_menu.my_landing_page',
  analytics: 'publisher.left_navigation.analytics',
  integrations: 'publisher.left_navigation.integrations',
  developer: 'publisher.app_shell.profile_menu.developer',
  visitMarketingSite: 'publisher.personal_settings.navigation.visit_marketing_site',
  superAdmin: 'publisher.header.menu.admin_dashboard.label',
  logOut: 'publisher.app_shell.profile_menu.log_out',
  upgrade: 'publisher.personal_settings.navigation.actions.upgrade_subscription'
} as const;

/**
 * Styles for the account summary slot inside the first `DropdownMenuGroup`.
 * `DropdownMenuGroupTitle` maps to the MenuSection title slot; typography is reset so this reads as identity + plan,
 * not a section label.
 */
const profileMenuAccountGroupCss = "pnohl7r";

/** Flex row for plan label and optional upgrade control. */
const planRowCss = "p8pnutf";

/** Text-style control for the billing upgrade affordance (matches legacy left-nav emphasis). */
const upgradeHintCss = "uft7v4u";

/**
 * Row container for one profile dropdown item: optional leading icon, label, and optional new-tab indicator.
 * Keeps the row behaving correctly inside CUI menu item layout and aligns icon with label text.
 */
const profileMenuItemRowCss = "p1ufbo43";

/**
 * Wraps the new-tab external-link icon so it stays at the trailing edge of the row while the label stays on the left.
 * Needed because CUI menu items lay out children in a way that naive “push to end” tricks do not always work.
 */
const profileMenuNewTabIconCss = "pqs950m";

/**
 * Renders one profile menu row: optional leading icon, label, and optional standard new-tab affordance at the end.
 *
 * @param labelText - Localized label for the row.
 * @param prefixIcon - Optional leading glyph for the row.
 * @param opensNewTab - When true, append the shared external-link icon at the end of the row.
 * @returns Content for a single `DropdownMenuItem` row.
 */
function ProfileMenuItemRowLayout({
  labelText,
  prefixIcon,
  opensNewTab = false
}: {
  labelText: string;
  prefixIcon?: UiIconGlyphComponent;
  opensNewTab?: boolean;
}) {
  const label = <LabelText size="large" color="standard" normalWeight>
      {labelText}
    </LabelText>;
  const prefixEl = prefixIcon != null ? <UiIcon color={colorIconStandard} icon={prefixIcon} size="small" title="" /> : null;
  return <span className={profileMenuItemRowCss}>
      {prefixEl}
      {label}
      {opensNewTab ? <span className={profileMenuNewTabIconCss} aria-hidden>
          <UiIcon color={colorIconSecondary} icon={IconExternalLink} size="small" title="" />
        </span> : null}
    </span>;
}
export type ProfileMenuContentProps = {
  /** User display name from session. */
  fullName: string;
  /** Organization name line under the user (may be empty). */
  businessName: string;
  /** Localized plan descriptor from billing helpers. */
  planLabel: string;
  /** Whether upgrade affordances are allowed upstream (e.g. non-enterprise); combined with admin/owner below. */
  showAccountMenuUpgrade: boolean;
  /** When true, show Super Admin entry — same signal as legacy header (`useGetUserQuery` → `is_super_admin`). */
  isSuperAdmin?: boolean;
};

/**
 * Profile/account menu body for the app-shell avatar dropdown (`DropdownMenuContent`).
 *
 * Renders the first group as account summary (name, organization, plan, optional upgrade), a second group of primary shortcuts
 * (profile, integrations, analytics, developer, optional super-admin, external links), a theme section when the UI theme
 * flag is on, and a final group with sign-out.
 *
 * Navigation targets mirror `DropDownUserMenu` / left navigation where applicable (`features/header/drop-down-user-menu.js`,
 * `features/left_navigation/config.js`). Settings IA routing uses `useProductRefreshSettingsIa` like other app-shell entry points.
 * Landing page URL comes from the same scheduling profiles payload as
 * `ProfileBookingPageLink` (`booking_url` on the personal `User` profile for `my_calendly` scope).
 *
 * Must render under `DropdownMenu` / `DropdownMenuProvider` so `useDropdownMenu` is defined
 * (e.g. upgrade closes the shell menu after billing dispatch).
 */
export const ProfileMenuContent: FC<ProfileMenuContentProps> = ({
  fullName,
  businessName,
  planLabel,
  showAccountMenuUpgrade,
  isSuperAdmin = false
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const {
    pathname
  } = location;
  const track = useTrack();
  const isAdminOrOwner = useSelector(isAdminOrOwnerSelector);
  const {
    onClose: closeDropdownMenu
  } = useDropdownMenu();
  const isPhoneLayout = useDeviceType('isPhone');
  /* Same gate as header user menu: settings IA flag plus product-refresh preview override (see `useProductRefreshSettingsIa`). */
  const isRebrandSettingsIa = useProductRefreshSettingsIa();
  /* Theme row is tied to its own flag alone — without it the menu is unchanged. */
  const showThemeRow = useFlag(UI_THEME_FLAG_KEY, false);
  const {
    hrefForSettingsModalEntry
  } = useSettingsNavigation();
  /* Admin hooks are plain JS — narrow typings so `setHasSeenNotification(false)` and logout submit stay callable in TS. */
  const [hasSeenNotification, setHasSeenNotification] = useHasSeenNotification() as [boolean, (value: boolean) => void];
  const [performSessionLogout] = useUserSessionLogout() as unknown as [() => void];
  const {
    data: schedulingProfiles
  } = useSchedulingFetchProfilesQuery({
    scopeType: 'my_calendly',
    scopeId: 'me',
    queryParams: {}
  });

  /** Same construction as `profileLinkTo` in `features/header/drop-down-user-menu.js`. */
  const profileLinkTo = hrefForSettingsModalEntry(SETTINGS_SECTION.profile, PROFILE_APP_PATH_LEGACY);

  /** Personal scheduling profile booking URL — same field as `ProfileBookingPageLink` in scheduling header. */
  const landingPageUrl = useMemo(() => {
    const results = schedulingProfiles?.results;
    if (!results) return undefined;
    const ids = selectProfileIds(results);
    let fallbackUrl: string | undefined;
    for (const id of ids) {
      const profile = selectProfileById(results, id);
      const url = profile?.booking_url;
      if (url) {
        if (profile.type === 'User') {
          return url;
        }
        if (!fallbackUrl) {
          fallbackUrl = url;
        }
      }
    }
    return fallbackUrl;
  }, [schedulingProfiles]);

  /** Upgrade hint visibility matches legacy left-nav: caller gate plus admin/owner. */
  const showUpgradeLikeLeftNav = showAccountMenuUpgrade && isAdminOrOwner;

  /** Records profile-dropdown navigation clicks per the CUI rebrand tracking plan. */
  const trackProfileDropdownNav = useCallback((linkKey: string) => {
    trackProfileDropdownNavigation(track, linkKey, pathname);
  }, [pathname, track]);

  /**
   * Opens the standard billing upgrade checkout flow with the same analytics entrypoint as legacy left-nav upgrade CTAs.
   * Closes the dropdown after dispatch so the modal is not obscured by the menu surface.
   */
  const openUpgradeModalLikeLeftNav = useCallback(() => {
    dispatch(setAnalyticsLocation(BILLING_ENTRYPOINTS.LEFT_NAV));
    track({
      name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
      properties: {
        action: 'opening',
        location: 'left_nav',
        modal_name: 'upgrade_plan',
        path: pathname
      }
    });

    /* Billing `moveToCheckout` is a thunk; default Dispatch typings here omit thunk middleware. */
    dispatch(moveToCheckout({
      analyticCheckoutFlowLocation: 'upgrade_prompt_left_nav'
    }) as never);
    closeDropdownMenu();
  }, [closeDropdownMenu, dispatch, pathname, track]);

  /** Mirrors `handleLogoutAction` in `features/header/drop-down-user-menu.js` (localStorage + POST sign-out). */
  const handleLogoutAction = useCallback(() => {
    window.localStorage.setItem('CALENDLY_AUTHENTICATED_USER_STATUS', 'false');
    if (hasSeenNotification) {
      setHasSeenNotification(false);
    }
    trackProfileDropdownNav('logout');
    performSessionLogout();
  }, [hasSeenNotification, performSessionLogout, setHasSeenNotification, trackProfileDropdownNav]);
  const goProfile = useCallback(() => {
    if (isRebrandSettingsIa && !confirmSettingsEntryNavigation()) {
      return;
    }
    trackProfileDropdownNav('profile');
    history.push(profileLinkTo);
  }, [history, isRebrandSettingsIa, profileLinkTo, trackProfileDropdownNav]);
  const goIntegrations = useCallback(() => {
    trackProfileDropdownNav('integration_and_apps');
    history.push(INTEGRATIONS_PATH);
  }, [history, trackProfileDropdownNav]);
  const goAnalytics = useCallback(() => {
    trackProfileDropdownNav('analytics');
    history.push(ORGANIZATION_REPORTING_PATH);
  }, [history, trackProfileDropdownNav]);
  return <>
      <DropdownMenuGroup>
        <DropdownMenuGroupTitle className={isPhoneLayout ? undefined : profileMenuAccountGroupCss}>
          <LabelText size="large" color="standard">
            {fullName}
          </LabelText>
          {businessName ? <BodyText size="small" color="muted">
              {businessName}
            </BodyText> : null}
          {/* Flex + column-gap only between plan tier and Upgrade; no fixed menu width (safe on mobile sheet). */}
          {planLabel ? <div className={planRowCss}>
              <BodyText size="small" color="muted" inline>
                {planLabel}
              </BodyText>
              {showUpgradeLikeLeftNav ? <BareButton type="button" className={upgradeHintCss} onClick={openUpgradeModalLikeLeftNav}>
                  {I18n.t(PROFILE_MENU_I18N_KEYS.upgrade)}
                </BareButton> : null}
            </div> : null}
        </DropdownMenuGroupTitle>
      </DropdownMenuGroup>
      <DropdownMenuGroup>
        <DropdownMenuItem data-analytics-id={navigationAnalyticsIdForProfileDropdown('profile')} onSelect={goProfile}>
          <ProfileMenuItemRowLayout prefixIcon={IconUserSquareRounded} labelText={I18n.t(PROFILE_MENU_I18N_KEYS.profile)} />
        </DropdownMenuItem>
        <DropdownMenuItem disabled={!landingPageUrl} data-analytics-id={navigationAnalyticsIdForProfileDropdown('landing_page')} onSelect={() => {
        trackProfileDropdownNav('landing_page');
        if (landingPageUrl) {
          window.open(landingPageUrl, '_blank', 'noopener,noreferrer');
        }
      }}>
          <ProfileMenuItemRowLayout prefixIcon={IconBrowser} opensNewTab labelText={I18n.t(PROFILE_MENU_I18N_KEYS.landingPage)} />
        </DropdownMenuItem>
        <DropdownMenuItem data-analytics-id={navigationAnalyticsIdForProfileDropdown('analytics')} onSelect={goAnalytics}>
          <ProfileMenuItemRowLayout prefixIcon={IconChartBar} labelText={I18n.t(PROFILE_MENU_I18N_KEYS.analytics)} />
        </DropdownMenuItem>
        <DropdownMenuItem data-analytics-id={navigationAnalyticsIdForProfileDropdown('integration_and_apps')} onSelect={goIntegrations}>
          <ProfileMenuItemRowLayout prefixIcon={IconApps} labelText={I18n.t(PROFILE_MENU_I18N_KEYS.integrations)} />
        </DropdownMenuItem>
        <DropdownMenuItem data-analytics-id={navigationAnalyticsIdForProfileDropdown('developer')} onSelect={() => {
        trackProfileDropdownNav('developer');
        window.open(DEVELOPER_DOCS_URL, '_blank', 'noopener,noreferrer');
      }}>
          <ProfileMenuItemRowLayout prefixIcon={IconCode} opensNewTab labelText={I18n.t(PROFILE_MENU_I18N_KEYS.developer)} />
        </DropdownMenuItem>
        <DropdownMenuItem data-analytics-id={navigationAnalyticsIdForProfileDropdown('visit_calendly')} onSelect={() => {
        trackProfileDropdownNav('visit_calendly');
        window.open(VISIT_MARKETING_SITE_URL, '_blank', 'noopener,noreferrer');
      }}>
          <ProfileMenuItemRowLayout prefixIcon={IconClick} opensNewTab labelText={I18n.t(PROFILE_MENU_I18N_KEYS.visitMarketingSite)} />
        </DropdownMenuItem>
        {isSuperAdmin ? <DropdownMenuItem data-calendly-label="super-admin-link" data-analytics-id={navigationAnalyticsIdForProfileDropdown('super_admin')} onSelect={() => {
        trackProfileDropdownNav('super_admin');
        window.open(SUPER_ADMIN_HREF, '_blank', 'noopener,noreferrer');
      }}>
            <ProfileMenuItemRowLayout prefixIcon={IconLock} opensNewTab labelText={I18n.t(PROFILE_MENU_I18N_KEYS.superAdmin)} />
          </DropdownMenuItem> : null}
      </DropdownMenuGroup>
      {showThemeRow ? <ProfileThemeRow /> : null}
      <DropdownMenuGroup>
        <DropdownMenuItem data-analytics-id={navigationAnalyticsIdForProfileDropdown('logout')} onSelect={handleLogoutAction}>
          <ProfileMenuItemRowLayout prefixIcon={IconLogout} labelText={I18n.t(PROFILE_MENU_I18N_KEYS.logOut)} />
        </DropdownMenuItem>
      </DropdownMenuGroup>
    </>;
};

require("./ProfileMenuContent.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./ProfileMenuContent.tsx");